import { Carton, Project, Panel, Hardware, SyncData, User } from '../types';

class OfflineStorageService {
  private dbName = 'FMS_DB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('projects')) {
          const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
          projectStore.createIndex('status', 'status', { unique: false });
        }

        if (!db.objectStoreNames.contains('cartons')) {
          const cartonStore = db.createObjectStore('cartons', { keyPath: 'id' });
          cartonStore.createIndex('projectId', 'projectId', { unique: false });
          cartonStore.createIndex('cartonCode', 'cartonCode', { unique: true });
          cartonStore.createIndex('status', 'status', { unique: false });
        }

        if (!db.objectStoreNames.contains('panels')) {
          const panelStore = db.createObjectStore('panels', { keyPath: 'id' });
          panelStore.createIndex('cartonId', 'cartonId', { unique: false });
        }

        if (!db.objectStoreNames.contains('hardware')) {
          const hardwareStore = db.createObjectStore('hardware', { keyPath: 'id' });
          hardwareStore.createIndex('cartonId', 'cartonId', { unique: false });
        }

        if (!db.objectStoreNames.contains('syncData')) {
          const syncStore = db.createObjectStore('syncData', { keyPath: 'id' });
          syncStore.createIndex('synced', 'synced', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }

        if (!db.objectStoreNames.contains('userData')) {
          db.createObjectStore('userData', { keyPath: 'key' });
        }
      };
    });
  }

  // Project Methods
  async saveProjects(projects: Project[]): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['projects'], 'readwrite');
    const store = transaction.objectStore('projects');

    for (const project of projects) {
      await this.promisifyRequest(store.put(project));
    }
  }

  async getProjects(): Promise<Project[]> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['projects'], 'readonly');
    const store = transaction.objectStore('projects');
    const request = store.getAll();

    return this.promisifyRequest(request);
  }

  async getProject(id: string): Promise<Project | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['projects'], 'readonly');
    const store = transaction.objectStore('projects');
    const request = store.get(id);

    return this.promisifyRequest(request);
  }

  // Carton Methods
  async saveCartons(cartons: Carton[]): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['cartons'], 'readwrite');
    const store = transaction.objectStore('cartons');

    for (const carton of cartons) {
      await this.promisifyRequest(store.put(carton));
    }
  }

  async getCartons(projectId?: string): Promise<Carton[]> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['cartons'], 'readonly');
    const store = transaction.objectStore('cartons');

    if (projectId) {
      const index = store.index('projectId');
      const request = index.getAll(projectId);
      return this.promisifyRequest(request);
    } else {
      const request = store.getAll();
      return this.promisifyRequest(request);
    }
  }

  async getCarton(id: string): Promise<Carton | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['cartons'], 'readonly');
    const store = transaction.objectStore('cartons');
    const request = store.get(id);

    return this.promisifyRequest(request);
  }

  async getCartonByCode(code: string): Promise<Carton | null> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['cartons'], 'readonly');
    const store = transaction.objectStore('cartons');
    const index = store.index('cartonCode');
    const request = index.get(code);

    return this.promisifyRequest(request);
  }

  async updateCarton(carton: Carton): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['cartons'], 'readwrite');
    const store = transaction.objectStore('cartons');
    await this.promisifyRequest(store.put(carton));
  }

  // Panel Methods
  async savePanels(panels: Panel[]): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['panels'], 'readwrite');
    const store = transaction.objectStore('panels');

    for (const panel of panels) {
      await this.promisifyRequest(store.put(panel));
    }
  }

  async getPanelsByCarton(cartonId: string): Promise<Panel[]> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['panels'], 'readonly');
    const store = transaction.objectStore('panels');
    const index = store.index('cartonId');
    const request = index.getAll(cartonId);

    return this.promisifyRequest(request);
  }

  async updatePanel(panel: Panel): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['panels'], 'readwrite');
    const store = transaction.objectStore('panels');
    await this.promisifyRequest(store.put(panel));
  }

  // Hardware Methods
  async saveHardware(hardware: Hardware[]): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['hardware'], 'readwrite');
    const store = transaction.objectStore('hardware');

    for (const item of hardware) {
      await this.promisifyRequest(store.put(item));
    }
  }

  async getHardwareByCarton(cartonId: string): Promise<Hardware[]> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['hardware'], 'readonly');
    const store = transaction.objectStore('hardware');
    const index = store.index('cartonId');
    const request = index.getAll(cartonId);

    return this.promisifyRequest(request);
  }

  async updateHardware(hardware: Hardware): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['hardware'], 'readwrite');
    const store = transaction.objectStore('hardware');
    await this.promisifyRequest(store.put(hardware));
  }

  // Sync Data Methods
  async addSyncData(syncData: SyncData): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['syncData'], 'readwrite');
    const store = transaction.objectStore('syncData');
    await this.promisifyRequest(store.put(syncData));
  }

  async getPendingSyncData(): Promise<SyncData[]> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['syncData'], 'readonly');
    const store = transaction.objectStore('syncData');
    
    // Get all items and filter by synced property
    const request = store.getAll();
    const allData = await this.promisifyRequest(request);
    return allData.filter(item => !item.synced);
  }

  async markSyncDataComplete(id: string): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['syncData'], 'readwrite');
    const store = transaction.objectStore('syncData');
    
    const getRequest = store.get(id);
    const syncData = await this.promisifyRequest(getRequest);
    
    if (syncData) {
      syncData.synced = true;
      await this.promisifyRequest(store.put(syncData));
    }
  }

  async clearSyncedData(): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['syncData'], 'readwrite');
    const store = transaction.objectStore('syncData');
    
    // Get all items and delete synced ones
    const request = store.getAll();
    const allData = await this.promisifyRequest(request);
    
    for (const item of allData) {
      if (item.synced) {
        await this.promisifyRequest(store.delete(item.id));
      }
    }
  }

  // User Data Methods
  async saveUserData(key: string, data: any): Promise<void> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['userData'], 'readwrite');
    const store = transaction.objectStore('userData');
    await this.promisifyRequest(store.put({ key, data }));
  }

  async getUserData(key: string): Promise<any> {
    if (!this.db) await this.init();
    
    const transaction = this.db!.transaction(['userData'], 'readonly');
    const store = transaction.objectStore('userData');
    const request = store.get(key);
    
    const result = await this.promisifyRequest(request);
    return result ? result.data : null;
  }

  // Utility Methods
  async clearAllData(): Promise<void> {
    if (!this.db) await this.init();
    
    const storeNames = ['projects', 'cartons', 'panels', 'hardware', 'syncData', 'userData'];
    const transaction = this.db!.transaction(storeNames, 'readwrite');
    
    for (const storeName of storeNames) {
      const store = transaction.objectStore(storeName);
      await this.promisifyRequest(store.clear());
    }
  }

  async getStorageUsage(): Promise<{
    projects: number;
    cartons: number;
    panels: number;
    hardware: number;
    syncData: number;
  }> {
    if (!this.db) await this.init();
    
    const counts = {
      projects: 0,
      cartons: 0,
      panels: 0,
      hardware: 0,
      syncData: 0,
    };

    const transaction = this.db!.transaction(['projects', 'cartons', 'panels', 'hardware', 'syncData'], 'readonly');
    
    for (const [key, storeName] of Object.entries({
      projects: 'projects',
      cartons: 'cartons',
      panels: 'panels',
      hardware: 'hardware',
      syncData: 'syncData',
    })) {
      const store = transaction.objectStore(storeName);
      const request = store.count();
      counts[key as keyof typeof counts] = await this.promisifyRequest(request);
    }

    return counts;
  }

  // Helper method to promisify IndexedDB requests
  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Check if online
  isOnline(): boolean {
    return navigator.onLine;
  }

  // Close database connection
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

export const offlineStorage = new OfflineStorageService();
export default offlineStorage; 